import { useState, useRef, useEffect } from 'react';

export const timeInitState = {
  centiseconds: 0,
  seconds: 0,
  minutes: 0,
  inAbsoluteSeconds: 0,
};

export interface TimeTrivia {
  centiseconds: number;
  seconds: number;
  minutes: number;
  inAbsoluteSeconds: number;
}

const useStopwatch = () => {
  const [time, setTime] = useState(timeInitState);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const updateTime = () => {
    if (startTimeRef.current !== null) {
      const elapsed = Date.now() - startTimeRef.current;
      const centiseconds = Math.floor(elapsed / 10) % 100;
      const seconds = Math.floor(elapsed / 1000) % 60;
      const minutes = Math.floor(elapsed / 60000);
      const inAbsoluteSeconds = Math.floor(elapsed / 1000);

      setTime({ centiseconds, seconds, minutes, inAbsoluteSeconds });
    }
  };

  const start = () => {
    if (intervalRef.current === null) {
      startTimeRef.current =
        Date.now() -
        (time.minutes * 60000 + time.seconds * 1000 + time.centiseconds * 10);
      updateTime();
      intervalRef.current = setInterval(updateTime, 100); // 10 fps (~centiseconds)
    }
  };

  const stop = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const restart = () => {
    stop();
    setTime(timeInitState);
    start();
  };

  useEffect(() => {
    return () => stop(); // Cleanup on unmount
  }, []);

  return { time, start, stop, restart };
};

export default useStopwatch;
